import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './AdminFiltersPopover.scss';

interface AdminFiltersPopoverProps {
  label?: string;
  activeCount: number;
  onReset?: () => void;
  children: ReactNode;
}

interface AnchorRect {
  top: number;
  left: number;
  bottom: number;
  right: number;
  width: number;
}

const PANEL_WIDTH = 280;
const PANEL_GUTTER = 8;

export const AdminFiltersPopover: React.FC<AdminFiltersPopoverProps> = ({
  label = 'Filters',
  activeCount,
  onReset,
  children,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);

  const recomputeAnchor = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setAnchor({
      top: rect.top,
      left: rect.left,
      bottom: rect.bottom,
      right: rect.right,
      width: rect.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    recomputeAnchor();
  }, [open, recomputeAnchor]);

  // Keep the popover anchored when the page scrolls or the viewport resizes.
  // Without this the panel stays at the original screen position while the
  // trigger drifts away. `passive: true` because we only read scroll state.
  useEffect(() => {
    if (!open) return;
    const handle = () => recomputeAnchor();
    window.addEventListener('scroll', handle, { passive: true, capture: true });
    window.addEventListener('resize', handle, { passive: true });
    return () => {
      window.removeEventListener('scroll', handle, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', handle);
    };
  }, [open, recomputeAnchor]);

  // Close on Escape — backdrop already handles outside clicks.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // Clamp the panel into the viewport. Right-align under the trigger by
  // default; if that would push it past the left edge, snap to a gutter.
  const computePanelStyle = (a: AnchorRect): React.CSSProperties => {
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : PANEL_WIDTH;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 600;
    const desiredLeft = a.right - PANEL_WIDTH;
    const left = Math.min(
      Math.max(PANEL_GUTTER, desiredLeft),
      Math.max(PANEL_GUTTER, viewportWidth - PANEL_WIDTH - PANEL_GUTTER)
    );
    // If the panel would overflow the viewport bottom, flip above the trigger.
    const desiredTop = a.bottom + PANEL_GUTTER;
    const top = desiredTop;
    return {
      position: 'fixed',
      top,
      left,
      maxHeight: Math.max(120, viewportHeight - top - PANEL_GUTTER),
      overflowY: 'auto',
    };
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="admin-filters-popover__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="admin-filters-popover__trigger-label">{label}</span>
        {activeCount > 0 && (
          <span className="admin-filters-popover__badge" aria-label={`${activeCount} active filters`}>
            {activeCount}
          </span>
        )}
      </button>

      {open && anchor &&
        createPortal(
          <>
            <div
              className="admin-filters-popover__backdrop"
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            <div
              className="admin-filters-popover__panel"
              role="dialog"
              aria-label={label}
              style={computePanelStyle(anchor)}
            >
              <div className="admin-filters-popover__panel-header">
                <span>{label}</span>
                <button
                  type="button"
                  className="admin-filters-popover__close"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="admin-filters-popover__panel-body">{children}</div>
              {onReset && (
                <div className="admin-filters-popover__panel-footer">
                  <button
                    type="button"
                    className="admin-filters-popover__reset"
                    onClick={() => {
                      onReset();
                      setOpen(false);
                    }}
                    disabled={activeCount === 0}
                  >
                    Reset
                  </button>
                </div>
              )}
            </div>
          </>,
          document.body
        )}
    </>
  );
};

export default AdminFiltersPopover;
